import { type ReactNode } from "react";
import {
  Check,
  Clock,
  AlertCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { Badge } from "@/shared/ui/core/Badge";

export type TimelineStatus =
  | "neutral"
  | "primary"
  | "accent"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "hold";

export interface TimelineItem {
  id: string | number;
  /** Primary event / step title */
  title: ReactNode;
  /** Timestamp or date string (e.g. "15:27:51 5/9/2026") */
  time?: string | ReactNode;
  /** Subtitle or meta info (e.g. "Người gửi: admin · Policy: POL-ENG-BOM") */
  subtitle?: ReactNode;
  /** Extended detail, note, comment or card content */
  detail?: ReactNode;
  /** Status tone for dot & badge */
  status?: TimelineStatus;
  /** Custom badge override */
  badge?: {
    label: string;
    tone?: "neutral" | "primary" | "accent" | "info" | "success" | "warning" | "danger" | "hold";
    className?: string;
  };
  /** Custom icon inside the node dot */
  icon?: ReactNode;
  /** Force hollow ring dot (matching approval history style) */
  hollow?: boolean;
  /** Custom action element (e.g. ChevronRight, link, button) */
  action?: ReactNode;
  /** Interactive click handler for the entire timeline node / card */
  onClick?: () => void;
  /** Highlight active/current step */
  active?: boolean;
  /** Children content underneath the item */
  children?: ReactNode;
}

export interface TimelineProps {
  /** List of timeline items */
  items: TimelineItem[];
  /** Compact spacing for drawer or side panel */
  dense?: boolean;
  /** Connecting line style */
  lineStyle?: "solid" | "dashed";
  /** Node indicator style: 'ring' (hollow circle) | 'icon' (badge with icon) | 'dot' (solid circle) */
  indicatorVariant?: "ring" | "icon" | "dot";
  /** Badge and action placement: 'inline' (next to title) or 'end' (right-aligned). Default: 'inline' */
  badgePlacement?: "inline" | "end";
  /** Vertical alignment of indicators: 'top' (top-aligned to first line) or 'center' (vertically centered to content). Default: 'center' */
  alignIndicators?: "top" | "center";
  /** Empty state message if items is empty */
  emptyText?: string;
  className?: string;
  "aria-label"?: string;
}

const statusClasses: Record<
  TimelineStatus,
  {
    ring: string;
    dot: string;
    iconBg: string;
    badgeTone: "neutral" | "primary" | "accent" | "info" | "success" | "warning" | "danger" | "hold";
  }
> = {
  neutral: {
    ring: "border-border bg-surface text-text-muted",
    dot: "bg-border",
    iconBg: "bg-surface text-text-muted border-border",
    badgeTone: "neutral",
  },
  primary: {
    ring: "border-primary bg-surface text-primary ring-4 ring-primary/15",
    dot: "bg-primary",
    iconBg: "bg-primary text-on-primary border-primary",
    badgeTone: "primary",
  },
  accent: {
    ring: "border-success bg-surface text-success",
    dot: "bg-success",
    iconBg: "bg-success text-on-primary border-success",
    badgeTone: "success",
  },
  info: {
    ring: "border-blue-500 bg-surface text-blue-600 ring-4 ring-blue-500/20",
    dot: "bg-blue-600",
    iconBg: "bg-blue-600 text-white border-blue-600 shadow-xs",
    badgeTone: "info",
  },
  success: {
    ring: "border-success bg-surface text-success",
    dot: "bg-success",
    iconBg: "bg-success text-on-primary border-success",
    badgeTone: "success",
  },
  warning: {
    ring: "border-warning bg-surface text-warning ring-4 ring-warning/15",
    dot: "bg-warning",
    iconBg: "bg-warning text-on-primary border-warning",
    badgeTone: "warning",
  },
  danger: {
    ring: "border-danger bg-surface text-danger ring-4 ring-danger/15",
    dot: "bg-danger",
    iconBg: "bg-danger text-on-primary border-danger",
    badgeTone: "danger",
  },
  hold: {
    ring: "border-danger bg-surface text-danger ring-4 ring-danger/15",
    dot: "bg-danger",
    iconBg: "bg-danger text-on-primary border-danger",
    badgeTone: "danger",
  },
};

/** Default icon based on status tone */
function defaultIconForStatus(status: TimelineStatus) {
  switch (status) {
    case "success":
      return <Check className="size-3 stroke-[3]" />;
    case "info":
      return <Loader2 className="size-3 animate-spin" />;
    case "accent":
    case "warning":
    case "primary":
      return <Clock className="size-3 stroke-[2.5]" />;
    case "danger":
      return <XCircle className="size-3 stroke-[2.5]" />;
    case "hold":
      return <AlertCircle className="size-3 stroke-[2.5]" />;
    case "neutral":
    default:
      return null;
  }
}

/**
 * Vertical Timeline / Progress Tracker component (Timeline / VerticalTimeline).
 * Standardized for approval history, shipment journey, batch tracking, audit logs.
 */
export function Timeline({
  items,
  dense = false,
  lineStyle = "solid",
  indicatorVariant = "ring",
  badgePlacement = "inline",
  alignIndicators = "center",
  emptyText = "Chưa có tiến trình nào",
  className,
  "aria-label": ariaLabel = "Tiến trình theo thời gian",
}: TimelineProps) {
  if (items.length === 0) {
    return (
      <div className="py-6 text-center text-caption text-text-muted">
        {emptyText}
      </div>
    );
  }

  const isDashed = lineStyle === "dashed";

  return (
    <ol
      aria-label={ariaLabel}
      className={cn("m-0 list-none p-0 relative", className)}
    >
      {items.map((item, index) => {
        const isFirst = index === 0;
        const isLast = index === items.length - 1;
        const status = item.status || "neutral";
        const config = statusClasses[status];
        const effectiveVariant = item.hollow ? "ring" : indicatorVariant;
        const isCenter = alignIndicators === "center";

        const lineTopClass =
          effectiveVariant === "icon"
            ? "top-4"
            : effectiveVariant === "dot"
              ? "top-3"
              : "top-3.5";

        const lineEndClass =
          effectiveVariant === "icon"
            ? "h-4"
            : effectiveVariant === "dot"
              ? "h-3"
              : "h-3.5";

        return (
          <li
            key={item.id}
            className={cn(
              "relative",
              dense ? "pb-3.5" : "pb-5",
              isLast && "pb-0",
            )}
          >
            {/* Vertical Connector Line */}
            {items.length > 1 && (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute left-[9px] -translate-x-1/2 w-0.5 z-0",
                  isFirst
                    ? isCenter
                      ? dense
                        ? "top-[calc(50%-6px)] bottom-0"
                        : "top-[calc(50%-10px)] bottom-0"
                      : `${lineTopClass} bottom-0`
                    : isLast
                      ? isCenter
                        ? "-top-px bottom-1/2"
                        : `-top-px ${lineEndClass}`
                      : "-top-px bottom-0",
                  isDashed
                    ? "border-l-2 border-dashed border-border"
                    : "bg-border",
                )}
              />
            )}

            <div className="relative flex items-center pl-7 min-w-0">
              {/* Node Indicator */}
              <div
                aria-hidden="true"
                className={cn(
                  "absolute left-[9px] -translate-x-1/2 z-10 flex items-center justify-center transition-colors select-none",
                  isCenter ? "top-1/2 -translate-y-1/2" : "top-1.5",
                  effectiveVariant === "icon"
                    ? cn(
                        "size-5 rounded-full border shadow-2xs text-[10px]",
                        config.iconBg,
                      )
                    : effectiveVariant === "dot"
                      ? cn("size-3 rounded-full ring-2 ring-surface", config.dot)
                      : cn(
                          "size-3.5 rounded-full border-[2.5px] bg-surface",
                          config.ring,
                        ),
                )}
              >
                {effectiveVariant === "icon" && (item.icon || defaultIconForStatus(status))}
              </div>

              {/* Content Body */}
              <div
                className={cn(
                  "min-w-0 flex-1 rounded-control transition-colors",
                  item.onClick &&
                    "group cursor-pointer px-2.5 py-1.5 -mx-1.5 hover:bg-bg/80 active:bg-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  item.active &&
                    (status === "info"
                      ? "bg-blue-500/10 px-2.5 py-1.5 -mx-1.5 ring-1 ring-blue-500/25"
                      : status === "accent"
                        ? "bg-accent/10 px-2.5 py-1.5 -mx-1.5 ring-1 ring-accent/30"
                        : "bg-primary/5 px-2.5 py-1.5 -mx-1.5 ring-1 ring-primary/20"),
                )}
                role={item.onClick ? "button" : undefined}
                tabIndex={item.onClick ? 0 : undefined}
                onClick={item.onClick}
                onKeyDown={
                  item.onClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          item.onClick?.();
                        }
                      }
                    : undefined
                }
              >
                {badgePlacement === "end" ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      {/* Optional Timestamp */}
                      {item.time && (
                        <div className="min-w-0 break-words font-mono text-caption tabular-nums text-text-muted leading-tight">
                          {item.time}
                        </div>
                      )}

                      {/* Title */}
                      <div className="min-w-0 break-words text-body font-semibold text-text leading-snug">
                        {item.title}
                      </div>

                      {/* Subtitle / Metadata */}
                      {item.subtitle && (
                        <div className="min-w-0 break-words text-caption text-text-secondary leading-relaxed">
                          {item.subtitle}
                        </div>
                      )}

                      {/* Detailed box / remark / comments */}
                      {item.detail && (
                        <div className="mt-1.5 rounded-control border border-border/60 bg-bg/60 px-3 py-2 text-caption text-text whitespace-pre-wrap">
                          {item.detail}
                        </div>
                      )}

                      {/* Custom children extension */}
                      {item.children}
                    </div>

                    {(item.badge || item.action) && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        {item.badge ? (
                          <Badge
                            tone={item.badge.tone || config.badgeTone}
                            label={item.badge.label}
                            className={item.badge.className}
                          />
                        ) : null}
                        {item.action}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Optional Timestamp */}
                    {item.time && (
                      <div className="min-w-0 break-words font-mono text-caption tabular-nums text-text-muted leading-tight">
                        {item.time}
                      </div>
                    )}

                    {/* Title & Badge Header */}
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="min-w-0 break-words text-body font-semibold text-text leading-snug">
                        {item.title}
                      </span>

                      {item.badge ? (
                        <Badge
                          tone={item.badge.tone || config.badgeTone}
                          label={item.badge.label}
                          className={item.badge.className}
                        />
                      ) : null}
                      {item.action}
                    </div>

                    {/* Subtitle / Metadata */}
                    {item.subtitle && (
                      <div className="min-w-0 break-words text-caption text-text-secondary leading-relaxed">
                        {item.subtitle}
                      </div>
                    )}

                    {/* Detailed box / remark / comments */}
                    {item.detail && (
                      <div className="mt-1.5 rounded-control border border-border/60 bg-bg/60 px-3 py-2 text-caption text-text whitespace-pre-wrap">
                        {item.detail}
                      </div>
                    )}

                    {/* Custom children extension */}
                    {item.children}
                  </div>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Alias for discoverability */
export const VerticalTimeline = Timeline;
